import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InvoiceOwnerModal — Cockpit quick-action.
 *
 * Flow (~10 seconds from Shannon's thumb to a live payment link):
 *   1. Pick project from active-projects dropdown
 *   2. Pick invoice package (Studio, 1BR, 2BR, 3BR, Deep Clean, Full, Custom)
 *   3. Type owner email
 *   4. Tap "Generate Link" → server creates invoice + Stripe session
 *   5. Payment URL auto-copies to clipboard; toast confirms "Paste into text/email"
 *
 * The 72-hour crew payout clock auto-starts the moment the owner pays that link.
 */
export default function InvoiceOwnerModal({ open, onClose }) {
  const [projects, setProjects] = useState([]);
  const [packages, setPackages] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [packageId, setPackageId] = useState("swift_turn_2bed");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {payment_url, amount, description}
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [pr, pk] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
          fetch(`${BACKEND_URL}/api/admin/invoice-owner/packages`, { credentials: "include" }).then(r => r.ok ? r.json() : { packages: [] }),
        ]);
        const projs = Array.isArray(pr) ? pr : (pr.projects || []);
        setProjects(projs);
        setProjectId(prev => prev || (projs[0]?.project_id || ""));
        setPackages(pk.packages || []);
      } catch { /* silent */ }
    })();
  }, [open]);

  if (!open) return null;

  const isCustom = packageId === "custom";

  const generate = async () => {
    setBusy(true); setError(""); setResult(null); setCopied(false);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/invoice-owner/create`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: projectId,
          package_id: packageId,
          owner_email: ownerEmail,
          origin_url: window.location.origin,
          custom_amount: isCustom ? parseFloat(customAmount || "0") : 0,
          notes,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Could not create invoice"); setBusy(false); return; }
      setResult(d);
      try {
        await navigator.clipboard.writeText(d.payment_url);
        setCopied(true);
      } catch { /* clipboard blocked — that's OK, the URL is visible below */ }
    } catch {
      setError("Network error. Try again.");
    }
    setBusy(false);
  };

  const reset = () => {
    setResult(null); setError(""); setCopied(false);
    setOwnerEmail(""); setCustomAmount(""); setNotes("");
  };

  return (
    <div
      data-testid="invoice-owner-modal-backdrop"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.75)", backdropFilter: "blur(6px)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "linear-gradient(180deg, #0F1729 0%, #0B1220 100%)", border: "1px solid rgba(0,220,120,0.35)", borderRadius: 14, padding: "1.6rem 1.7rem", maxWidth: 520, width: "100%", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(0,0,0,0.55)" }}
        data-testid="invoice-owner-modal"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <p style={{ color: "#00DC78", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", margin: 0 }}>COCKPIT QUICK ACTION</p>
            <h2 style={{ color: "#F3F4F6", fontSize: "1.35rem", fontWeight: 800, margin: "4px 0 6px" }}>💰 Invoice Owner</h2>
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", margin: 0 }}>
              Generate a Stripe payment link. Owner pays → the 72-hour crew clock auto-starts.
            </p>
          </div>
          <button
            data-testid="invoice-owner-close"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}
          >×</button>
        </div>

        {result ? (
          <div style={{ marginTop: 18 }}>
            <div style={{ background: "rgba(0,220,120,0.08)", border: "1px solid rgba(0,220,120,0.35)", borderRadius: 10, padding: "1rem", marginBottom: 12 }}>
              <div style={{ color: "#00DC78", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>✓ LINK READY {copied ? "· COPIED TO CLIPBOARD" : ""}</div>
              <div style={{ color: "#F3F4F6", fontSize: "1.1rem", fontWeight: 800 }}>${Number(result.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div style={{ color: "#ADBED1", fontSize: "0.82rem", marginTop: 2 }}>{result.description}</div>
            </div>
            <div style={{ background: "rgba(15,20,30,0.6)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "0.75rem", marginBottom: 12, wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.78rem", color: "#5AC8D8" }}>
              {result.payment_url}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                data-testid="invoice-owner-copy-again"
                onClick={async () => { try { await navigator.clipboard.writeText(result.payment_url); setCopied(true); } catch { /* noop */ } }}
                style={btnPrimary}
              >{copied ? "✓ Copied" : "📋 Copy Link"}</button>
              <a
                href={`mailto:${ownerEmail}?subject=Payment%20Link%20—%20Moovin%20Forward&body=${encodeURIComponent(`Hi,\n\nHere's the secure payment link for your project (${result.description}):\n\n${result.payment_url}\n\nOnce received, our 72-hour crew mobilization clock starts automatically.\n\nThanks,\nMoovin Forward`)}`}
                data-testid="invoice-owner-email"
                style={btnSecondary}
              >✉️ Email Owner</a>
              <button
                data-testid="invoice-owner-another"
                onClick={reset}
                style={btnGhost}
              >+ Another</button>
            </div>
            <p style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 12, textAlign: "center" }}>
              Payment lands in Stripe → owner sees a receipt → your Cockpit lights up → 72h crew clock starts.
            </p>
          </div>
        ) : (
          <div style={{ marginTop: 18, display: "grid", gap: 12 }}>
            <Field label="Project">
              <select
                data-testid="invoice-owner-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                style={inputStyle}
              >
                {projects.length === 0 && <option value="">No active projects</option>}
                {projects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.address || p.project_id} {p.status ? `· ${p.status}` : ""}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Package">
              <select
                data-testid="invoice-owner-package"
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                style={inputStyle}
              >
                {packages.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.label}{p.amount ? ` · $${Number(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}
                  </option>
                ))}
              </select>
            </Field>

            {isCustom && (
              <Field label="Custom amount (USD, $100–$50,000)">
                <input
                  data-testid="invoice-owner-custom-amount"
                  type="number"
                  min="100"
                  max="50000"
                  step="1"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="e.g. 2750"
                  style={inputStyle}
                />
              </Field>
            )}

            <Field label="Owner email (for the mailto shortcut)">
              <input
                data-testid="invoice-owner-email-input"
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="owner@example.com"
                style={inputStyle}
              />
            </Field>

            <Field label="Notes (optional — visible only to you)">
              <input
                data-testid="invoice-owner-notes"
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Rush turn — needs by Fri"
                style={inputStyle}
              />
            </Field>

            {error && (
              <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5", padding: "0.6rem 0.8rem", borderRadius: 8, fontSize: "0.82rem" }}>
                {error}
              </div>
            )}

            <button
              data-testid="invoice-owner-generate"
              onClick={generate}
              disabled={busy || !projectId || (isCustom && !customAmount)}
              style={{ ...btnPrimary, opacity: (busy || !projectId || (isCustom && !customAmount)) ? 0.55 : 1, cursor: (busy || !projectId) ? "wait" : "pointer" }}
            >
              {busy ? "Generating…" : "→ Generate Payment Link"}
            </button>
            <p style={{ color: "#7E92A8", fontSize: "0.72rem", margin: 0, textAlign: "center" }}>
              Amount is locked server-side per package. You cannot be tricked into short-billing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4, textTransform: "uppercase" }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: "100%",
  background: "rgba(15,20,30,0.6)",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8,
  padding: "0.7rem 0.85rem",
  color: "#F3F4F6",
  fontSize: "0.92rem",
  outline: "none",
};

const btnPrimary = {
  background: "linear-gradient(90deg, #00DC78, #00B364)",
  color: "#062617",
  border: "none",
  borderRadius: 10,
  padding: "0.85rem 1.2rem",
  fontWeight: 800,
  fontSize: "0.95rem",
  cursor: "pointer",
  flex: 1,
  textAlign: "center",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const btnSecondary = {
  background: "rgba(90,200,216,0.15)",
  color: "#5AC8D8",
  border: "1px solid rgba(90,200,216,0.4)",
  borderRadius: 10,
  padding: "0.85rem 1rem",
  fontWeight: 700,
  fontSize: "0.9rem",
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const btnGhost = {
  background: "transparent",
  color: "#ADBED1",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 10,
  padding: "0.85rem 1rem",
  fontWeight: 700,
  fontSize: "0.9rem",
  cursor: "pointer",
};
